/* @flow */

import { getCCPA } from './ccpautils';
import { isGPC } from './browserutil';
import { logDebug } from './logger';
import { isFunction } from './coreutils';
// Use CmpApi from @iabgpp/cmpapi to handle GPP CMP API v1.1 specification
// CmpApi automatically handles window.__gpp registration and all standard commands
// Ref: IAB GPP CMP API docs – see "CmpApi" and section/field definitions.
//      npm: @iabgpp/cmpapi
import { CmpApi } from '@iabgpp/cmpapi';

/**
 * @module utils/gpputils
 * @description GPP (Global Privacy Platform) CMP API v1.1 implementation
 *
 * This module implements the GPP CMP API v1.1 specification using @iabgpp/cmpapi:
 * - CmpApi handles all standard commands: ping, addEventListener, removeEventListener, hasSection, getSection, getField
 * - Converts USPRIVACY (CCPA) string to GPP sections (uspv1 and usca)
 * - Supports GPC signal integration
 * - Init on JSAPI script load; refresh via initOrUpdateGPPAPI() / refreshPrivacySignals()
 *
 * Ref: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/blob/main/Core/CMP%20API%20Specification.md
 *
 * @requires utils/ccpautils
 * @requires utils/browserutil
 * @requires utils/logger
 * @requires @iabgpp/cmpapi
 */

// GPP Section IDs
// Ref: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/blob/main/Core/CMP%20API%20Specification.md
const GPP_SECTION_USPV1 = 6; // US Privacy v1 section
const GPP_SECTION_USCA = 8; // US California section

/** CmpApi instance - set on first initOrUpdateGPPAPI() call */
// $FlowFixMe[unclear-type] - CmpApi is any-typed from @iabgpp/cmpapi
let cmpApiInstance: ?any = null;

/** Cached CCPA value - tracks last value used to update GPP */
let lastCachedCCPA: ?string = null;

/**
 * Minimal stub function for window.__gpp that CmpApi expects
 * CmpApi will replace this stub with its own implementation
 */
// CmpApi replaces this stub; signature accepts arbitrary GPP command args.
// eslint-disable-next-line no-unused-vars, no-empty-function
function gppStubFunction(..._args: Array<unknown>) {}

/**
 * Updates GPP sections from USPRIVACY string
 * Converts standard CCPA USPRIVACY string (format: "1YYN") to GPP uspv1 and usca sections
 * @param {string} uspString - USPRIVACY string (format: "1YYN")
 * @param {boolean} bIgnoreCache - If true, bypass cache check and force update (default: false)
 * @returns {void}
 */
function updateGPPFromUSP(
  uspString: string,
  bIgnoreCache: boolean = false
): void {
  const cmpApi = cmpApiInstance;
  if (!cmpApi) {
    logDebug('GPP: Cannot update - CmpApi instance not initialized');
    return;
  }

  // Check if CCPA value has changed - skip update if unchanged (unless cache is ignored)
  if (!bIgnoreCache && uspString === lastCachedCCPA) {
    return;
  }

  try {
    // Normalize USPRIVACY string - use '1---' (CCPA does not apply) as default for invalid/missing values
    const normalizedUspString =
      !uspString || uspString.length !== 4 ? '1---' : uspString;

    // Destructure string for better readability
    const [versionChar, noticeChar, optOutChar, lspaChar] = normalizedUspString;

    // Position 1: Version
    const version = Number.parseInt(versionChar, 10) || 1;

    // Check GPC signal - if enabled, it overrides opt-out preferences
    // GPC indicates opt-out, so OptOutSale should be "Y" and Notice should be "Y"
    const gpcEnabled = isGPC();
    const finalNotice = gpcEnabled ? 'Y' : noticeChar;

    // Critical: When CCPA applies (not '1---'), OptOutSale cannot be '-' per US Privacy String spec
    // Ref: https://github.com/InteractiveAdvertisingBureau/USPrivacy/blob/master/CCPA/US%20Privacy%20String.md
    // "The third character position (Opt-Out Sale) cannot be unknown (must never include a hyphen) when CCPA applies."
    // COM-7677 Accept-only: undecided (e.g. 1Y--) defaults to OptOutSale Y (restricted), not N.
    const ccpaApplies = normalizedUspString !== '1---';
    let finalOptOut = gpcEnabled ? 'Y' : optOutChar;
    if (ccpaApplies && finalOptOut === '-' && !gpcEnabled) {
      finalOptOut = 'Y';
    }

    // Set USPv1 section (Section 6) - use characters directly from CCPA string
    cmpApi.setFieldValue('uspv1', 'Version', version);
    cmpApi.setFieldValue('uspv1', 'Notice', finalNotice);
    cmpApi.setFieldValue('uspv1', 'OptOutSale', finalOptOut);
    cmpApi.setFieldValue('uspv1', 'LspaCovered', lspaChar);

    // Set California section (Section 8)
    // Position 2: Notice (maps to both sale and sharing opt-out notices)
    // According to CA spec: 0=Not Applicable, 1=Yes notice provided, 2=No notice not provided
    // USPRIVACY: Y=notice provided, N=no notice, -=not applicable
    let caNotice = 0; // Default to 0 (Not Applicable) for '-'
    if (noticeChar === 'Y') {
      caNotice = 1; // Yes, notice was provided
    } else if (noticeChar === 'N') {
      caNotice = 2; // No, notice was not provided
    }

    // Position 3: Opt-out (maps to both sale and sharing opt-outs)
    // According to CA spec: 0=Not Applicable, 1=Opted Out, 2=Did Not Opt Out
    // USPRIVACY: Y=opted out, N=did not opt out, -=not applicable
    // Critical: When CCPA applies (not '1---'), OptOutSale cannot be '-' per US Privacy String spec
    // COM-7677: undecided OptOutSale '-' with notice provided defaults to 1 (Opted Out)
    // IMPORTANT: Per CA spec, SaleOptOut/SharingOptOut must be 0 (Not Applicable) when:
    //   - SaleOptOutNotice/SharingOptOutNotice is 0 (Not Applicable), OR
    //   - SaleOptOutNotice/SharingOptOutNotice is 2 (No notice provided)
    // OptOut can only be 1 or 2 when Notice is 1 (Yes, notice provided)
    let caOptOut = 0; // Default to 0 (Not Applicable)

    // Only set opt-out value if notice was provided (caNotice === 1)
    if (caNotice === 1) {
      // Notice was provided, so opt-out value is meaningful
      if (optOutChar === 'Y') {
        caOptOut = 1; // Opted Out
      } else if (optOutChar === 'N') {
        caOptOut = 2; // Did Not Opt Out
      } else if (ccpaApplies && optOutChar === '-' && !gpcEnabled) {
        // Accept-only: undecided defaults to Opted Out (1), not Did Not Opt Out
        caOptOut = 1;
      }
      // If optOutChar is '-' and CCPA doesn't apply, caOptOut remains 0 (Not Applicable)
    }
    // If caNotice is 0 or 2, caOptOut remains 0 (Not Applicable) per spec

    // Apply GPC override for California section
    // GPC overrides both notice (sets to 1) and opt-out (sets to 1)
    const finalCaOptOut = gpcEnabled ? 1 : caOptOut;
    const finalCaNotice = gpcEnabled ? 1 : caNotice;

    // California section version is independent of USPRIVACY version
    // Hardcode to 1 per CA spec v1.0
    // Ref: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/blob/main/Sections/US-States/CA/GPP%20Extension%3A%20California%20Privacy%20Technical%20Specification.md
    const uscaVersion = 1;
    cmpApi.setFieldValue('usca', 'Version', uscaVersion);
    cmpApi.setFieldValue('usca', 'SaleOptOutNotice', finalCaNotice);
    cmpApi.setFieldValue('usca', 'SharingOptOutNotice', finalCaNotice);
    cmpApi.setFieldValue('usca', 'SaleOptOut', finalCaOptOut);
    cmpApi.setFieldValue('usca', 'SharingOptOut', finalCaOptOut);

    // Set other required California fields to 0 (not applicable/unknown)
    // NOTE: California fields don't accept -1, use 0 instead
    cmpApi.setFieldValue('usca', 'SensitiveDataLimitUseNotice', 0);
    cmpApi.setFieldValue(
      'usca',
      'SensitiveDataProcessing',
      [0, 0, 0, 0, 0, 0, 0, 0, 0]
    );
    cmpApi.setFieldValue('usca', 'KnownChildSensitiveDataConsents', [0, 0]);
    cmpApi.setFieldValue('usca', 'PersonalDataConsents', 0);

    // Set MSPA fields - not an MSPA signed publisher
    // Ref: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform
    // MspaCoveredTransaction: 1=Yes, 2=No, 0=Not Applicable
    // Since we are not an MSPA signed publisher, set to 2 (No)
    cmpApi.setFieldValue('usca', 'MspaCoveredTransaction', 2);
    cmpApi.setFieldValue('usca', 'MspaOptOutOptionMode', 0);
    cmpApi.setFieldValue('usca', 'MspaServiceProviderMode', 0);

    // Set GPC subsection - GPC signal detected from browser
    // Ref: https://raw.githubusercontent.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/refs/heads/main/Sections/US-States/CA/GPP%20Extension%3A%20California%20Privacy%20Technical%20Specification.md
    // GPC subsection: SubsectionType=1 (GPC), Gpc=Boolean (true/false)
    // Always set Gpc field to indicate whether GPC signal was detected
    cmpApi.setFieldValue('usca', 'Gpc', gpcEnabled);

    // Fire section change events
    cmpApi.fireSectionChange('uspv1');
    cmpApi.fireSectionChange('usca');

    const gppString = cmpApi.getGppString();
    logDebug(
      `GPP: updated usprivacy=${lastCachedCCPA ?? ''} -> ${uspString} gppString=${gppString || ''}`
    );

    // Cache the original value (not normalized) after successful update
    lastCachedCCPA = uspString;
  } catch (error) {
    logDebug(`GPP: Error updating GPP from USPRIVACY string: ${error}`);
  }
}

/**
 * Checks if window.__gpp needs a stub function
 * @returns {boolean} True if stub is needed
 */
function needsGPPStub(): boolean {
  return (
    typeof window !== 'undefined' &&
    (!window.__gpp || typeof window.__gpp !== 'function')
  );
}

/**
 * Creates a stub function for window.__gpp that CmpApi expects
 * CmpApi will replace this stub with its own implementation
 * @returns {void}
 */
function createGPPStub(): void {
  // $FlowFixMe[incompatible-use] - window.__gpp is any
  // Create stub function with properties that CmpApi expects
  // $FlowFixMe[incompatible-use] - window.__gpp will be replaced by CmpApi
  // Add properties that EventListenerQueue and CmpApi expect
  // $FlowFixMe[prop-missing]
  gppStubFunction.queue = [];
  // $FlowFixMe[prop-missing]
  gppStubFunction.events = [];
  // $FlowFixMe[incompatible-use] - window.__gpp will be replaced by CmpApi
  window.__gpp = gppStubFunction;
}

/**
 * Configures CmpApi instance with status and applicable sections
 * @param {any} apiInstance - The CmpApi instance to configure
 * @returns {void}
 */
function configureCMPApi(apiInstance: CmpApi) {
  // $FlowFixMe[unclear-type] - CmpApi is any-typed from @iabgpp/cmpapi
  // Set CMP status
  apiInstance.setCmpStatus('loaded');
  apiInstance.setCmpDisplayStatus('hidden');
  apiInstance.setSignalStatus('ready');

  // Signal which GPP sections we support
  // Ref: https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform/blob/main/Core/CMP%20API%20Specification.md
  apiInstance.setApplicableSections([GPP_SECTION_USPV1, GPP_SECTION_USCA]);
}

/**
 * Initializes CmpApi instance with proper stub and configuration
 * @returns {any} The initialized CmpApi instance
 */
function initializeCMPApi() {
  // $FlowFixMe[unclear-type] - CmpApi is any-typed from @iabgpp/cmpapi
  if (needsGPPStub()) {
    // Reset instance state when reinitializing
    cmpApiInstance = null;
    lastCachedCCPA = null;
    // Create stub function that CmpApi expects
    createGPPStub();
  }

  // Create CmpApi instance
  // CmpApi constructor automatically registers window.__gpp and handles all standard commands
  // CMP ID 0 = Unknown CMP ID, Version 1
  const apiInstance = new CmpApi(0, 1);

  // Configure CMP status and applicable sections
  configureCMPApi(apiInstance);

  return apiInstance;
}

/**
 * Initializes or updates GPP CMP API
 * First call: creates CmpApi and populates GPP from USPRIVACY/GPC
 * Later calls: refreshes GPP sections if USPRIVACY changed (or when bIgnoreCache)
 * @param {boolean} bIgnoreCache - If true, bypass cache check and force update (default: false)
 * @returns {void}
 */
export function initOrUpdateGPPAPI(bIgnoreCache: boolean = false): void {
  // Idempotency when our CmpApi is live and window.__gpp is still registered.
  // Foreign stubs (no cmpApiInstance) fall through — CmpApi replaces them on first init.
  if (
    cmpApiInstance &&
    typeof window !== 'undefined' &&
    isFunction(window.__gpp)
  ) {
    logDebug('GPP: API already initialized, checking for CCPA changes');
    updateGPPFromUSP(getCCPA(true), bIgnoreCache);
    return;
  }

  if (cmpApiInstance) {
    cmpApiInstance = null;
    lastCachedCCPA = null;
  }

  try {
    const apiInstance = initializeCMPApi();
    cmpApiInstance = apiInstance;

    // Populate GPP data from USPRIVACY string
    updateGPPFromUSP(getCCPA(true), bIgnoreCache);

    // Fire initial signalStatus event
    apiInstance.fireEvent('signalStatus', 'ready');

    logDebug('GPP: API initialized successfully');
  } catch (error) {
    logDebug(`GPP: Error initializing API: ${error}`);
  }
}

/**
 * Gets GPP data formatted for OpenRTB bid requests
 * Returns GPP string and section IDs in OpenRTB format
 * Ref: OpenRTB spec for regs.gpp and regs.gpp_sid fields
 * @returns {{gpp: string, gpp_sid: number[]}} Object with gpp (encoded string) and gpp_sid (section IDs array), or empty object if unavailable
 */
export function getGPPForOpenRTB(): { gpp: string, gpp_sid: number[] } {
  // Return empty object if GPP not initialized
  if (!cmpApiInstance) {
    // eslint-disable-next-line camelcase -- OpenRTB regs.gpp_sid
    return { gpp: '', gpp_sid: [] };
  }

  try {
    // $FlowFixMe[unclear-type] - CmpApi is any-typed from @iabgpp/cmpapi
    // Get encoded GPP string directly from CmpApi instance
    const gppString = cmpApiInstance.getGppString();

    // Return empty object if GPP string is empty or invalid
    if (!gppString || typeof gppString !== 'string') {
      // eslint-disable-next-line camelcase -- OpenRTB regs.gpp_sid
      return { gpp: '', gpp_sid: [] };
    }

    // Use the applicable sections we set during initialization
    // These match the sections configured in setApplicableSections()
    const sectionIds = [GPP_SECTION_USPV1, GPP_SECTION_USCA];

    return {
      gpp: gppString,
      gpp_sid: sectionIds // eslint-disable-line camelcase
    };
  } catch (error) {
    logDebug(`GPP: Error getting GPP data for OpenRTB: ${error}`);
    // eslint-disable-next-line camelcase -- OpenRTB regs.gpp_sid
    return { gpp: '', gpp_sid: [] };
  }
}
